// ./src/components/features/Vehicle/VehicleCardOdometer/VehicleCardOdometer.tsx
import React from "react";
import { Group, Stack, Text, Tooltip, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { UnitDisplayMode } from "@/utils/unitDisplayHelpers";
import { AppIcon } from "@/components/generic/AppIcon";

import { UnitToggle } from "../UnitToggle";

// ============================================
// Types
// ============================================

export type OdometerLayout = "horizontal" | "vertical";

export interface VehicleCardOdometerProps {
  /** Current odometer reading (already converted to display unit) */
  odometer: number;

  /** Monthly mileage change (already converted to display unit, optional) */
  monthlyChange?: number | null;

  /** Distance unit for display (e.g., "km", "mi") */
  unit?: string;

  /** Size variant */
  size?: "sm" | "md" | "lg";

  /** Layout: "horizontal" (default) or "vertical" (for tight spaces) */
  layout?: OdometerLayout;

  /** Whether to show the monthly change */
  showChange?: boolean;

  /** Whether to show the unit toggle button */
  showUnitToggle?: boolean;

  /** Current unit display mode (for toggle) */
  unitMode?: UnitDisplayMode;

  /** Volume unit for toggle label */
  volumeUnit?: string;

  /** Callback when unit toggle is clicked */
  onUnitToggle?: () => void;

  /** Custom class name */
  className?: string;
}

// ============================================
// Constants
// ============================================

const SIZE_CONFIG = {
  sm: {
    odometerSize: "lg" as const,
    odometerWeight: 700,
    changeSize: "xs" as const,
    iconSize: 12,
    gap: "xs" as const,
  },
  md: {
    odometerSize: "xl" as const,
    odometerWeight: 700,
    changeSize: "sm" as const,
    iconSize: 14,
    gap: "xs" as const,
  },
  lg: {
    odometerSize: "1.75rem",
    odometerWeight: 700,
    changeSize: "sm" as const,
    iconSize: 16,
    gap: "sm" as const,
  },
};

// ============================================
// Helper Functions
// ============================================

/**
 * Format odometer value with thousands separators and unit
 */
const formatOdometerValue = (value: number, unit: string): string => {
  const formatted = Math.round(value).toLocaleString("en-US");
  return `${formatted} ${unit}`;
};

/**
 * Format distance change value
 */
const formatChangeValue = (value: number): string => {
  return Math.round(value).toLocaleString("en-US");
};

// ============================================
// Component
// ============================================

/**
 * Displays odometer reading with optional monthly change indicator.
 * 
 * Values are expected to be pre-converted to the display unit.
 * No conversion is performed in this component.
 *
 * Supports two layouts:
 * - "horizontal": Odometer and change side by side (default)
 * - "vertical": Odometer on top, change below (for compact spaces)
 * 
 * When showUnitToggle is true and onUnitToggle is provided,
 * displays a small toggle button to switch between user and car unit preferences.
 */
export const VehicleCardOdometer: React.FC<VehicleCardOdometerProps> = ({
  odometer,
  monthlyChange,
  unit = "km",
  size = "md",
  layout = "horizontal",
  showChange = true,
  showUnitToggle = false,
  unitMode = "user",
  volumeUnit = "l",
  onUnitToggle,
  className,
}) => {
  const { t } = useTranslation();
  const config = SIZE_CONFIG[size];

  const hasChange =
    showChange &&
    monthlyChange !== null &&
    monthlyChange !== undefined &&
    monthlyChange > 0;

  const formattedOdometer = formatOdometerValue(odometer, unit);
  const formattedChange = hasChange ? formatChangeValue(monthlyChange) : null;

  // Change indicator element
  const changeIndicator = hasChange ? (
    <Tooltip
      label={t("vehicle.mileageThisMonth", {
        distance: formattedChange,
        unit,
      })}
      withArrow
      position={layout === "vertical" ? "bottom" : "right"}
    >
      <Group
        gap={3}
        wrap="nowrap"
        style={{ color: "var(--mantine-color-green-6)" }}
      >
        <AppIcon iconName="TrendingUp" size={config.iconSize} stroke={2} />
        <Text size={config.changeSize} fw={500} ff="var(--ff-mono)">
          +{formattedChange}
        </Text>
      </Group>
    </Tooltip>
  ) : null;

  // Unit toggle element (only when units differ)
  const unitToggleElement =
    showUnitToggle && onUnitToggle ? (
      <Box mt={4}>
        <UnitToggle
          mode={unitMode}
          distanceUnit={unit}
          volumeUnit={volumeUnit}
          onToggle={onUnitToggle}
          size="sm"
        />
      </Box>
    ) : null;

  // ============================================
  // Vertical Layout
  // ============================================

  if (layout === "vertical") {
    return (
      <Stack gap={2} align="flex-end" className={className}>
        {/* Odometer value */}
        <Text
          size={config.odometerSize}
          fw={config.odometerWeight}
          ff="var(--ff-mono)"
          lh={1.1}
          style={{ letterSpacing: "-0.02em" }}
        >
          {formattedOdometer}
        </Text>

        {/* Monthly change indicator */}
        {changeIndicator}

        {/* Unit toggle (below change indicator) */}
        {unitToggleElement}
      </Stack>
    );
  }

  // ============================================
  // Horizontal Layout (Default)
  // ============================================

  return (
    <Stack gap={2} className={className}>
      <Group gap={config.gap} wrap="nowrap">
        {/* Odometer value */}
        <Text
          size={config.odometerSize}
          fw={config.odometerWeight}
          ff="var(--ff-mono)"
          style={{ letterSpacing: "-0.02em" }}
        >
          {formattedOdometer}
        </Text>

        {/* Monthly change indicator */}
        {changeIndicator}
      </Group>

      {/* Unit toggle (below the main row) */}
      {unitToggleElement}
    </Stack>
  );
};